import { Avatar } from "@mui/material";
import BaseRating from "../BaseRating";
import WorkOrderStatus from "../../WorkOrderStatus";
import dayjs from "dayjs";
import { useState } from "react";
import UserProfileLink from "../UserProfileLink";

const feedbackAuthorName = (addedBy) =>
  addedBy?.companyName || `${addedBy?.firstName || ""} ${addedBy?.lastName || ""}`.trim();

const BaseFeedback = ({ feedbackReceived, feedbackGiven, workOrder, isDefaultOpen = true }) => {
  const [isViewMore, setIsViewMore] = useState(isDefaultOpen);
  // A work order can carry only one of the two feedbacks. Show whichever exists as the
  // primary block, so a lone "given" feedback isn't rendered as an empty received card.
  const primaryFeedback = feedbackReceived || feedbackGiven;
  const secondaryFeedback = feedbackReceived ? feedbackGiven : null;
  const optionsReceived = [
    ...(primaryFeedback?.likedOptions || []),
    ...(primaryFeedback?.problemOptions || []),
  ];

  if (!primaryFeedback) {
    return (
      <div className="bg-light border rounded-md p-6 text-center">
        <p className="text-gray-500">No feedback</p>
      </div>
    );
  }

  return (
    <div className="bg-light border rounded-md p-6 ">
      {workOrder && (
        <div className="flex justify-between items-center mb-4">
          <div>
            <h6 className="font-semibold !text-primary capitalize">
              {workOrder?.title} <span className="text-xs font-light">({workOrder.category})</span>
            </h6>
            <div className="flex items-center mt-1">
              <p className="text-green-700">
                <i className="fa-solid fa-location-dot text-green-700 mr-1"></i>
                {workOrder.address}
              </p>
              <p className="text-green-700 ml-8">
                <i className="fa-light fa-car text-green-700 mr-1"></i>
                {workOrder.distance?.toFixed(2)} km
              </p>
            </div>
          </div>

          <div>
            <WorkOrderStatus className="py-1" status={workOrder.status} />
            <p className="mt-1">{dayjs(primaryFeedback?.createdAt).fromNow()}</p>
          </div>
        </div>
      )}
      <div className="flex max-w-[1250px]">
        <UserProfileLink
          as="div"
          userId={primaryFeedback?.addedBy?.id}
          search="?direct=true"
          className="shrink-0"
          linkClassName="cursor-pointer"
        >
          <Avatar sx={{ width: 50, height: 50 }} />
        </UserProfileLink>
        <div className="ml-3">
          <UserProfileLink
            as="h6"
            userId={primaryFeedback?.addedBy?.id}
            search="?direct=true"
            className="text-dark font-medium w-fit"
            linkClassName="cursor-pointer hover:underline"
          >
            {feedbackAuthorName(primaryFeedback?.addedBy)}
          </UserProfileLink>
          <p className="text-sm  text-primary mb-3">{primaryFeedback?.comment}</p>
          <BaseRating rating={primaryFeedback?.rating} size="large" />

          <div className="flex flex-wrap gap-x-6 gap-y-3 mt-2 items-end">
            {optionsReceived?.map((option, index) => (
              <p className="bg-white text-primary py-1 px-4 rounded-md" key={index}>
                {option}
              </p>
            ))}
            {secondaryFeedback && (
              <p
                className="text-primary text-[10px] cursor-default"
                onClick={() => setIsViewMore(!isViewMore)}
              >
                {isViewMore ? "View Less" : "View More"}
                <i
                  className={`fa-solid fa-angle-${
                    isViewMore ? "up" : "down"
                  } ml-1 text-primary cursor-pointer`}
                ></i>
              </p>
            )}
          </div>

          {secondaryFeedback && isViewMore && <FeedbackGiven feedbackGiven={secondaryFeedback} />}
        </div>
      </div>
    </div>
  );
};

export default BaseFeedback;

const FeedbackGiven = ({ feedbackGiven }) => {
  const optionsGiven = [
    ...(feedbackGiven?.likedOptions || []),
    ...(feedbackGiven?.problemOptions || []),
  ];

  return (
    <div className="flex border-l-4 border-neutral-300 pl-3 mt-8 max-w-[1100px]">
      <UserProfileLink
        as="div"
        userId={feedbackGiven?.addedBy?.id}
        search="?direct=true"
        className="shrink-0"
        linkClassName="cursor-pointer"
      >
        <Avatar />
      </UserProfileLink>
      <div className="ml-3">
        <div>
          <div>
            <UserProfileLink
              userId={feedbackGiven?.addedBy?.id}
              search="?direct=true"
              className="text-sm font-normal muted w-fit"
              linkClassName="cursor-pointer hover:underline"
            >
              {feedbackAuthorName(feedbackGiven?.addedBy)}
            </UserProfileLink>
            <p className="text-xs mb-3">{feedbackGiven?.comment}</p>
            <BaseRating rating={feedbackGiven?.rating} size="medium" />
          </div>
          <p className="mt-1">{dayjs(feedbackGiven?.createdAt).fromNow()}</p>
        </div>
        {optionsGiven?.length > 0 && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
            {optionsGiven?.map((option, index) => (
              <p className="bg-white text-primary py-1 px-4 rounded-md" key={index}>
                {option}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
